import React, { useState } from 'react';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { FOCUS_RING_INSET } from 'Components/DesignSystem/focusRing';

interface ConfigSectionAccordionProps {
  title: string;
  name: string;
  /** A setting in this section blocks Save: the section stays open so the error is on screen
   * when Save says "Fix the highlighted fields". */
  hasError?: boolean;
  children: React.ReactNode;
}

/** One collapsible Configuration section (open by default). Purely presentational —
 * the caller decides what goes inside and whether to render the section at all. */
const ConfigSectionAccordion: React.FC<ConfigSectionAccordionProps> = ({ title, name, hasError = false, children }) => {
  const [userOpen, setOpen] = useState(true);
  const open = userOpen || hasError;
  const testId = `resolve-config-section-${name}`;
  const uid = React.useId();
  const regionId = `${uid}-region`;

  return (
    <div className="tw-rounded-srf tw-border tw-border-edge tw-bg-surface-raised tw-shadow-ds-sm tw-mb-2 tw-overflow-hidden">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => setOpen(!open)}
        data-testid={testId}
        className={`tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-px-4 tw-py-3 tw-text-left tw-select-none ${FOCUS_RING_INSET}`}
      >
        <span
          aria-hidden="true"
          className={`tw-inline-block tw-shrink-0 tw-text-accent-bold tw-text-[12px] motion-safe:tw-transition-transform motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${
            open ? 'tw-rotate-90' : ''
          }`}
        >
          ▶
        </span>
        <Typography variant="title-header" as="h3" className="tw-text-ink">
          {title}
        </Typography>
      </button>

      {open && (
        <div role="region" id={regionId} className="tw-px-4 tw-pb-3">
          <div className="tw-flex tw-flex-col">{children}</div>
        </div>
      )}
    </div>
  );
};

export default ConfigSectionAccordion;
